import { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '@/services/api';
import type { LeaderboardEntry } from '@the-wall/shared';
import { UserBadges } from '@/components/UserBadges';
import { UsernameText } from '@/components/UsernameText';

interface LeaderboardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function LeaderboardIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}

const TOP3_STYLES = {
  1: {
    badge: 'bg-wall-highlight/20 text-wall-highlight border border-wall-highlight/50 shadow-[0_0_12px_rgba(255,215,0,0.25)]',
    row: 'bg-wall-highlight/5 border-l-2 border-wall-highlight/60',
  },
  2: {
    badge: 'bg-wall-muted/20 text-wall-muted border border-wall-muted/50',
    row: 'bg-wall-muted/5 border-l-2 border-wall-muted/40',
  },
  3: {
    badge: 'bg-amber-700/30 text-amber-400/90 border border-amber-500/40',
    row: 'bg-amber-500/5 border-l-2 border-amber-500/40',
  },
} as const;

function LeaderboardList({ entries, emptyMessage }: { entries: LeaderboardEntry[]; emptyMessage: string }) {
  if (entries.length === 0) {
    return (
      <p className="text-wall-muted text-sm py-6 text-center">{emptyMessage}</p>
    );
  }
  return (
    <ul className="divide-y divide-wall-border/50">
      {entries.map((entry) => {
        const isTop3 = entry.rank <= 3;
        const top3Style = isTop3 ? TOP3_STYLES[entry.rank as 1 | 2 | 3] : null;
        return (
          <li
            key={`${entry.rank}-${entry.username}`}
            className={`flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-r-lg ${
              top3Style ? top3Style.row : ''
            }`}
          >
            <span className="w-9 shrink-0 flex justify-center items-center">
              {isTop3 ? (
                <span
                  className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold leading-none ${top3Style!.badge}`}
                  aria-hidden
                >
                  {entry.rank}
                </span>
              ) : (
                <span className="text-sm font-medium text-wall-muted">
                  {entry.rank}
                </span>
              )}
            </span>
            <span className="h-8 w-8 shrink-0 rounded-full overflow-hidden bg-wall-border/50 flex items-center justify-center">
              {entry.profilePicture ? (
                <img
                  src={entry.profilePicture}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-xs font-medium text-wall-muted leading-none">
                  {entry.username.slice(0, 2).toUpperCase()}
                </span>
              )}
            </span>
            <span className="flex-1 min-w-0 flex items-center gap-1.5 overflow-hidden">
              <UsernameText
                text={entry.username}
                color={entry.usernameColor ?? null}
                effect={entry.usernameEffect ?? null}
                className="truncate text-wall-text font-medium"
              />
              <UserBadges userId={entry.userId} className="shrink-0" />
            </span>
            <span className="shrink-0 text-wall-muted text-sm tabular-nums">
              {entry.count} {entry.count === 1 ? 'pixel' : 'pixels'}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function LeaderboardModal({ isOpen, onClose }: LeaderboardModalProps) {
  const [daily, setDaily] = useState<LeaderboardEntry[]>([]);
  const [allTime, setAllTime] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'daily' | 'alltime'>('daily');

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    },
    [handleClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      setLoading(true);
      Promise.all([api.getLeaderboard('daily', 50), api.getLeaderboard('alltime', 50)])
        .then(([d, a]) => {
          setDaily(d);
          setAllTime(a);
        })
        .catch(() => {
          setDaily([]);
          setAllTime([]);
        })
        .finally(() => setLoading(false));
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md min-h-full"
        onClick={handleClose}
        aria-hidden
      />
      <div
        className="relative bg-wall-surface border border-wall-border rounded-2xl shadow-2xl w-full max-w-sm max-h-[85dvh] flex flex-col overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-4 pt-4 pb-3 border-b border-wall-border/60 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LeaderboardIcon className="w-5 h-5 text-wall-accent" />
            <h2 className="text-lg font-semibold text-wall-text tracking-tight">
              Top painters
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 -mr-2 text-wall-muted hover:text-wall-text rounded-lg hover:bg-wall-border/30 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="shrink-0 flex border-b border-wall-border/50">
          <button
            type="button"
            onClick={() => setActiveTab('daily')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'daily'
                ? 'text-wall-accent border-b-2 border-wall-accent'
                : 'text-wall-muted hover:text-wall-text'
            }`}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('alltime')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'alltime'
                ? 'text-wall-accent border-b-2 border-wall-accent'
                : 'text-wall-muted hover:text-wall-text'
            }`}
          >
            All time
          </button>
        </div>

        <div className="h-[420px] overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-wall-accent border-t-transparent" />
            </div>
          ) : activeTab === 'daily' ? (
            <LeaderboardList
              entries={daily}
              emptyMessage="No pixels placed today yet."
            />
          ) : (
            <LeaderboardList
              entries={allTime}
              emptyMessage="No pixels placed yet."
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
